<template>
  <Teleport to="body">
    <Transition name="scrim">
      <div
        v-if="open"
        class="fixed inset-0 z-[94] bg-black/10 tablet-h:hidden"
        @click="close"
      />
    </Transition>

    <Transition name="drawer">
      <aside
        v-if="open"
        class="fixed inset-y-0 left-0 z-[95] flex w-[86vw] max-w-[368px] flex-col rounded-r-[20px] bg-white shadow-[0_0_40px_rgba(0,0,0,0.12)] tablet-h:hidden"
        :style="dragStyle"
        @touchstart.passive="onTouchStart"
        @touchmove.passive="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div
          class="flex-1 overflow-y-auto overscroll-contain px-4"
          :style="{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }"
        >
          <div class="text-[13px] leading-[14px] text-gray-500">Account</div>

          <MenuAccountCard class="mt-2.5" />

          <MenuUsageCard
            class="mt-5"
            @action="onAction"
            @navigate="onNavigate"
          />
        </div>

        <div
          class="shrink-0"
          :style="{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }"
        >
          <div class="h-px bg-gray-800/10" />

          <nav class="pt-2">
            <MenuRow
              v-if="tier === 'pro'"
              :icon="MembershipThumbnail"
              @click="onMembership"
            >
              Dacr Membership card
            </MenuRow>
            <MenuRow :icon="ArchivedIcon" to="/archive" @click="close">
              Archived registrations
            </MenuRow>
            <MenuRow :icon="GearIcon" to="/settings" @click="close">
              Settings
            </MenuRow>

            <div class="my-1.5 ml-[60px] h-px bg-gray-800/10" />

            <MenuRow
              :icon="HelpIcon"
              href="https://help.dacr.com"
              @click="close"
            >
              Help Center
            </MenuRow>
            <MenuRow :icon="FileTextIcon" href="/resources" @click="close">
              Resources
            </MenuRow>

            <div class="my-1.5 ml-[60px] h-px bg-gray-800/10" />

            <MenuRow :icon="SignOutIcon" @click="onSignOut"> Sign out </MenuRow>
          </nav>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  ArchivedIcon,
  FileTextIcon,
  GearIcon,
  HelpIcon,
  SignOutIcon,
} from '@dacr/icons'

import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useUploadStore } from '@/features/upload/store'
import MenuAccountCard from './MenuAccountCard.vue'
import MenuUsageCard from './MenuUsageCard.vue'
import MenuRow from './MenuRow.vue'
import MembershipThumbnail from './MembershipThumbnail.vue'
import { useMenuModel, type MenuCta } from './useMenuModel'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const router = useRouter()
const uploadStore = useUploadStore()
const { tier } = useMenuModel()

const close = () => emit('update:open', false)

const onMembership = () => {
  close()
  openDialog(Dialog.DacrMembership)
}

const onAction = (action: MenuCta['action']) => {
  close()
  openModal(
    action === 'upgradePro' ? Modals.UpgradeToPro : Modals.UpgradeBundleLimit
  )
}

const onNavigate = (path: string) => {
  close()
  router.push(path)
}

const onSignOut = () => {
  close()
  uploadStore.showUploadBar = false
  router.push('/signout')
}

const dragX = ref(0)
const dragging = ref(false)
let startX = 0
let startY = 0
let horizontal: boolean | null = null

const dragStyle = computed(() =>
  dragging.value
    ? { transform: `translateX(${dragX.value}px)`, transition: 'none' }
    : undefined
)

const onTouchStart = (event: TouchEvent) => {
  startX = event.touches[0].clientX
  startY = event.touches[0].clientY
  horizontal = null
  dragging.value = false
}

const onTouchMove = (event: TouchEvent) => {
  const dx = event.touches[0].clientX - startX
  const dy = event.touches[0].clientY - startY

  if (horizontal === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
    horizontal = Math.abs(dx) > Math.abs(dy)
  }

  if (!horizontal) return

  dragging.value = true
  dragX.value = Math.min(0, dx)
}

const onTouchEnd = () => {
  const shouldClose = dragX.value < -80
  dragging.value = false
  dragX.value = 0
  if (shouldClose) close()
}

watch(
  () => props.open,
  (value) => {
    document.body.style.overflow = value ? 'hidden' : ''
  }
)
</script>

<style scoped>
.scrim-enter-active,
.scrim-leave-active {
  transition: opacity 0.25s ease;
}
.scrim-enter-from,
.scrim-leave-to {
  opacity: 0;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(-100%);
}
</style>
